import React, { useState } from "react";
import { useScrollReveal } from "./useScrollReveal";

const FAQS = [
	{
		q: "Is COMPS.red legal to use?",
		a: "Absolutely. COMPS.red works like Evernote Web Clipper or Pinterest's Save button — when you visit a property listing, you can save the publicly displayed details to your personal notebook. Federal courts have consistently upheld the right to access publicly available data. In hiQ Labs v. LinkedIn (2022), the Ninth Circuit ruled that accessing publicly available information does not violate the Computer Fraud and Abuse Act. The Supreme Court's Van Buren v. United States (2021) decision further confirmed that using publicly available data in ways a website might not prefer is not a federal crime. Every action in COMPS.red is initiated by you, the user, on data that is freely visible to anyone with a browser.",
	},
	{
		q: "Who is COMPS.red for?",
		a: "Everyone. Whether you're a first-time homebuyer comparing neighborhoods, a seller researching your home's value, a real estate agent building presentations, or an investor analyzing renovation deals — COMPS.red gives you the same organizational tools that were previously only available through expensive MLS subscriptions or institutional platforms.",
	},
	{
		q: "What does '.red' mean?",
		a: "'.red' stands for 'Real Estate, Decentralized.' It's a real internet domain extension — COMPS.red is a real website, just like any .com. We're building the .red ecosystem as a home for AI-powered tools that put real estate data back in the hands of everyday people.",
	},
	{
		q: "What browsers does COMPS.red support?",
		a: "COMPS.red is a Chrome extension that works on any Chromium-based browser including Google Chrome, Microsoft Edge, Brave, and Arc. We plan to support Firefox and Safari in the future.",
	},
	{
		q: "Does it work with sites other than Zillow?",
		a: "Yes! COMPS.red supports saving listings from Zillow, Redfin, Realtor.com, and Homes.com. You can also manually add properties from any source.",
	},
	{
		q: "How does real-time sync work?",
		a: "COMPS.red uses Firebase real-time database technology. When you or a teammate makes a change, it syncs across all connected devices in under 2 seconds. Changes are also persisted offline and synced when you reconnect.",
	},
	{
		q: "Can I use it offline?",
		a: "Yes. COMPS.red uses an IndexedDB-backed offline queue. You can view your decks, edit notes, and run ROI calculations offline. Changes sync automatically when your internet connection returns.",
	},
	{
		q: "What property data can I save?",
		a: "You can save property details (address, price, beds/baths/sqft), photos, notes, documents, ROI calculations, pipeline stage, priority, and custom tags. All data stays synced across your team.",
	},
	{
		q: "How is my data secured?",
		a: "Your data is stored in Google Firebase with enterprise-grade security. All connections are encrypted via TLS. Firebase Security Rules ensure users can only access their own workspace data. We never sell or share your data.",
	},
	{
		q: "Can I control team member permissions?",
		a: "Absolutely. COMPS.red offers 8 granular workspace roles from Viewer (read-only) to Owner (full admin). You can control who can edit properties, manage decks, invite members, and more.",
	},
];

export function FAQSection() {
	const ref = useScrollReveal();
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	const toggle = (i: number) => {
		setOpenIndex(openIndex === i ? null : i);
	};

	return (
		<section id="faq" className="py-20 md:py-28" ref={ref}>
			<div className="mx-auto max-w-3xl px-6">
				<div className="scroll-reveal mb-12 text-center">
					<h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
						Frequently asked questions
					</h2>
					<p className="mx-auto mt-4 max-w-xl text-lg text-zinc-400">
						Everything you need to know about COMPS.red.
					</p>
				</div>

				<div className="scroll-reveal space-y-2">
					{FAQS.map((faq, i) => (
						<div
							key={i}
							className="rounded-xl border border-white/5 bg-white/[0.02]"
						>
							<button
								onClick={() => toggle(i)}
								className="flex w-full items-center justify-between p-5 text-left"
							>
								<span className="text-sm font-medium text-white pr-4">
									{faq.q}
								</span>
								<svg
									className={`h-5 w-5 shrink-0 text-zinc-500 transition-transform duration-200 ${
										openIndex === i ? "rotate-180" : ""
									}`}
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={2}
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M19 9l-7 7-7-7"
									/>
								</svg>
							</button>
							{openIndex === i && (
								<div className="border-t border-white/5 px-5 pb-5 pt-3">
									<p className="text-sm leading-relaxed text-zinc-400">
										{faq.a}
									</p>
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
